import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Icon,
  VStack,
  Text,
  Link,
  Box,
  Image,
  Code,
  HStack,
  Input as ChakraInput,
  Alert,
  AlertIcon,
  useClipboard
} from "@chakra-ui/react";
import { MdPlayCircleFilled } from "react-icons/md";
import { saveToken } from "utils/auth";
import { useUser } from "context/user";
import { login, validateMfa, validateBackupCode } from "api/webservice/auth";
import { setupTwoFactor, verifyTwoFactorSetup, TwoFactorSetupResponse } from "api/webservice/twoFactor";
import { useToast } from "hooks";
import { Form, Input } from "components";

type LoginFormData = {
  username: string;
  password: string;
};

type MfaFormData = {
  code: string;
};

type BackupFormData = {
  backupCode: string;
};

export const LoginForm: React.FC = () => {
  const { setUser } = useUser()!;
  const toast = useToast();

  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [showBackupInput, setShowBackupInput] = useState(false);

  // 2FA Setup state
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const { onCopy, hasCopied } = useClipboard(
    backupCodes ? backupCodes.join("\n") : ""
  );

  const loginForm = useForm<LoginFormData>();
  const mfaForm = useForm<MfaFormData>();
  const backupForm = useForm<BackupFormData>();

  const validateUsername = (value: string) => {
    if (!value) return "Username is required";
    return true;
  };

  const validatePassword = (value: string) => {
    if (!value) return "Password is required";
    return true;
  };

  const handleLoginSuccess = (token: string, profile: any) => {
    if (token && profile) {
      saveToken(token);
      setUser(profile);
      window.location.replace("/");
    } else {
      toast({
        title: "Problem logging in",
        description: "Please refresh the page and try again.",
        status: "error"
      });
    }
  };

  /**
   * Handle a successful authentication response from any login step (password,
   * TOTP, or backup code). When the backend reports an expired password it
   * returns a single-purpose reset token instead of a session token; in that
   * case route the user to the reset flow with the token. For 2FA users this
   * `passwordExpired` payload only arrives after the MFA step, so this helper is
   * shared by all of the submit handlers.
   */
  const handleAuthResponse = (response: any): void => {
    if (response?.passwordExpired && response.resetToken) {
      toast({
        title: "Password expired",
        description:
          response.message ||
          "Your password has expired. Please reset your password.",
        status: "warning"
      });
      window.location.replace(
        `/reset-password?token=${encodeURIComponent(response.resetToken)}`
      );
      return;
    }

    handleLoginSuccess(response.token, response.profile);
  };

  /**
   * Surface an error from a login step. A 403 lockout response carries a
   * human-readable message (including the remaining time) in
   * `response.data.response`, which we show directly to the user.
   */
  const handleLoginError = (err: any, fallbackDescription: string): void => {
    const isLockout = err?.response?.status === 403;
    toast({
      title: isLockout ? "Account locked" : "Problem logging in",
      description: err.response?.data?.response || fallbackDescription,
      status: isLockout ? "warning" : "error"
    });
  };

  const submitLogin = async ({ username, password }: LoginFormData): Promise<void> => {
    const regex = new RegExp("([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|\"\(\[\]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|\[[\t -Z^-~]*])");
    if (regex.test(username)) {
      toast({
        title: "Problem logging in",
        description: "Please log in with your username, not email.",
        status: "error"
      });
      return;
    }

    try {
      const response = await login(username, password);

      if (response.mfaRequired) {
        setMfaToken(response.mfaToken);
      } else if (response.setupRequired) {
        // Clear any existing session before starting setup
        saveToken("");
        setSetupToken(response.setupToken);
        saveToken(response.setupToken);
        await initiateSetup();
      } else {
        // Covers both a normal session token and the `passwordExpired` redirect
        // for non-2FA (client) users.
        handleAuthResponse(response);
      }
    } catch (err: any) {
      handleLoginError(err, "Please refresh the page and try again.");
    }
  };

  const initiateSetup = async () => {
    setSetupLoading(true);
    try {
      const data = await setupTwoFactor();
      setSetupData(data);
      setBackupCodes(data.backupCodes);
    } catch (err: any) {
      toast({
        title: "Setup failed",
        description: err.response?.data?.response || "Failed to start 2FA setup.",
        status: "error"
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!setupCode || setupCode.length !== 6) {
      toast({ title: "Invalid code", description: "Enter a 6-digit code.", status: "warning" });
      return;
    }

    setSetupLoading(true);
    try {
      await verifyTwoFactorSetup(setupCode);
      toast({ title: "Success", description: "2FA is now enabled. Please log in again.", status: "success" });
      saveToken("");
      setSetupToken(null);
      setSetupData(null);
      setSetupCode("");
      setBackupCodes(null);
    } catch (err: any) {
      toast({
        title: "Verification failed",
        description: err.response?.data?.response || "Invalid code.",
        status: "error"
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const submitMfaCode = async ({ code }: MfaFormData): Promise<void> => {
    if (!mfaToken) return;

    try {
      const response = await validateMfa(mfaToken, code);
      // `passwordExpired` can arrive here (only after the MFA step for 2FA users).
      handleAuthResponse(response);
    } catch (err: any) {
      handleLoginError(err, "Invalid code. Please try again.");
    }
  };

  const submitBackupCode = async ({ backupCode }: BackupFormData): Promise<void> => {
    if (!mfaToken) return;

    try {
      const response = await validateBackupCode(mfaToken, backupCode);
      // `passwordExpired` can arrive here too (post-MFA for backup-code users).
      handleAuthResponse(response);
    } catch (err: any) {
      handleLoginError(err, "Invalid backup code.");
    }
  };

  // 2FA SETUP FLOW
  if (setupToken && setupData) {
    return (
      <Box maxH="60vh" overflowY="auto" key="setup-form" px={1}>
        <VStack spacing={4}>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            Set Up Two-Factor Authentication
          </Text>
          <Text fontSize="sm" textAlign="center" color="gray.300">
            Two-factor authentication is required. Scan the QR code below with your authenticator app.
          </Text>

          <Box textAlign="center" my={2}>
            <Image
              src={setupData.qrCodeDataUrl}
              alt="2FA QR Code"
              maxW="180px"
              mx="auto"
            />
          </Box>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Can&apos;t scan? Enter manually:{" "}
            <Code fontSize="xs">{setupData.secret}</Code>
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={2}>
            Enter the 6-digit code from your app:
          </Text>
          <HStack>
            <ChakraInput
              placeholder="000000"
              value={setupCode}
              onChange={e => setSetupCode(e.target.value)}
              maxLength={6}
              maxW="150px"
            />
            <Button
              colorScheme="blue"
              onClick={handleVerifySetup}
              isLoading={setupLoading}
            >
              Verify & Enable
            </Button>
          </HStack>

          {backupCodes && (
            <Box mt={3}>
              <Alert status="warning" borderRadius="md" mb={2} bgColor="#FFFBEB">
                <AlertIcon />
                <Text color="#92400E" fontSize="xs">
                  Save these backup codes in a secure place. Each code can only be used once and can help you access your account if you&apos;re unable to use your authenticator app.
                </Text>
              </Alert>
              <Box
                bg="gray.600"
                p={3}
                borderRadius="md"
                fontFamily="mono"
                fontSize="xs"
              >
                {backupCodes.map((code, i) => (
                  <Text key={i}>{code}</Text>
                ))}
              </Box>
              <Button size="xs" mt={2} onClick={onCopy}>
                {hasCopied ? "Copied!" : "Copy All Codes"}
              </Button>
            </Box>
          )}
        </VStack>
      </Box>
    );
  }

  // Loading while setup initiates
  if (setupToken && !setupData) {
    return (
      <VStack spacing={4} key="setup-loading">
        <Text fontSize="md" textAlign="center">Setting up two-factor authentication...</Text>
      </VStack>
    );
  }

  // MFA backup code input
  if (mfaToken && showBackupInput) {
    return (
      <Form key="backup-form" onSubmit={backupForm.handleSubmit(submitBackupCode)}>
        <VStack spacing={6}>
          <Text fontSize="md" textAlign="center">
            Enter one of your backup codes
          </Text>

          <Input
            name="backupCode"
            label="Backup Code"
            hideLabel
            register={backupForm.register({ required: "Backup code is required" })}
            placeholder=" "
            autoComplete="off"
            errorMessage={backupForm.errors.backupCode?.message}
          />

          <Button
            mt={4}
            disabled={backupForm.formState.isSubmitting}
            fontWeight="600"
            type="submit"
            width="full"
          >
            Verify Backup Code
          </Button>

          <Link
            color="blue.500"
            fontSize="sm"
            onClick={() => setShowBackupInput(false)}
          >
            Use authenticator code instead
          </Link>
        </VStack>
      </Form>
    );
  }

  // MFA TOTP code input
  if (mfaToken) {
    return (
      <Form key="mfa-form" onSubmit={mfaForm.handleSubmit(submitMfaCode)}>
        <VStack spacing={6}>
          <Text fontSize="md" textAlign="center">
            Enter the 6-digit code from your authenticator app
          </Text>

          <Input
            name="code"
            label="Verification Code"
            hideLabel
            register={mfaForm.register({
              required: "Code is required",
              pattern: { value: /^\d{6}$/, message: "Must be 6 digits" }
            })}
            placeholder=" "
            type="text"
            autoComplete="one-time-code"
            errorMessage={mfaForm.errors.code?.message}
          />

          <Button
            mt={4}
            disabled={mfaForm.formState.isSubmitting}
            rightIcon={
              <Icon as={MdPlayCircleFilled} w={6} h={6} color="blue.500" />
            }
            fontWeight="600"
            type="submit"
            width="full"
          >
            Verify
          </Button>

          <Link
            color="blue.500"
            fontSize="sm"
            onClick={() => setShowBackupInput(true)}
          >
            Use a backup code instead
          </Link>
        </VStack>
      </Form>
    );
  }

  // Standard login form
  return (
    <Form key="login-form" onSubmit={loginForm.handleSubmit(submitLogin)}>
      <VStack spacing={6}>
        <Input
          name="username"
          label="Username"
          hideLabel
          register={loginForm.register({ validate: validateUsername })}
          placeholder="Username"
          inputIcon="user"
          errorMessage={loginForm.errors.username?.message}
        />

        <Input
          name="password"
          label="Password"
          hideLabel
          placeholder="Password"
          type="password"
          inputIcon="password"
          register={loginForm.register({ validate: validatePassword })}
          errorMessage={loginForm.errors.password?.message}
        />

        <Button
          mt={8}
          disabled={loginForm.formState.isSubmitting}
          rightIcon={
            <Icon as={MdPlayCircleFilled} w={6} h={6} color="blue.500" />
          }
          fontWeight="600"
          type="submit"
        >
          Sign In
        </Button>
      </VStack>
    </Form>
  );
};
